<template>
  <aside
    class="h-screen flex flex-col transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] relative z-50 shadow-2xl select-none border-r border-white/5"
    :class="[isCollapsed ? 'w-24' : 'w-72']"
    :style="{
      background: 'radial-gradient(circle at top left, rgba(30, 41, 59, 1), rgba(15, 23, 42, 1))',
      color: '#f8fafc'
    }"
  >
    <button 
      @click="isCollapsed = !isCollapsed"
      class="absolute -right-3 top-10 w-6 h-6 bg-indigo-500 rounded-full border border-white/10 flex items-center justify-center text-white hover:bg-indigo-400 transition-all duration-300 shadow-[0_0_15px_rgba(99,102,241,0.4)] z-[60]"
    >
      <svg 
        class="w-3.5 h-3.5 transition-transform duration-500" 
        :class="{ 'rotate-180': isCollapsed }"
        viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"
      >
        <path d="m15 18-6-6 6-6"/>
      </svg>
    </button>

    <div class="h-24 flex items-center px-6 mb-2">
      <div class="flex items-center gap-3 overflow-hidden">
        <div class="min-w-[42px] h-[42px] flex items-center justify-center bg-white to-purple-500/20 rounded-xl border border-white/10 shadow-inner">
          <img 
            src="../assets/vague_bleue_petite.png" 
            class="h-6 object-contain transition-all duration-500"
            :class="[isCollapsed ? 'scale-110' : 'scale-100']" 
            alt="Logo" 
          />
        </div>
        <span v-if="!isCollapsed" class="font-bold text-xl tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-400 animate-in fade-in duration-700">
          <img 
            src="../assets/logo_medialink2017_final_blanc.png" 
            class="h-6 object-contain transition-all duration-500"
            :class="[isCollapsed ? 'scale-110' : 'scale-100']" 
            alt="Logo" 
          />
        </span>
      </div>
    </div>

    <nav class="flex-1 px-4 space-y-8 overflow-y-auto scrollbar-none py-2">
      
      <div class="space-y-2">
        <h3 v-if="!isCollapsed" class="px-2 text-[10px] font-bold uppercase tracking-[0.2em] text-indigo-400/60 mb-4">
          Infrastructure
        </h3>
        <div class="space-y-1">
          <RouterLink
            v-for="item in infrastructureMenu"
            :key="item.label"
            :to="item.to"
            class="flex items-center gap-4 rounded-xl px-4 py-3 text-sm transition-all duration-300 relative text-gray-400 border border-transparent hover:bg-white/5 hover:text-white group"
            :active-class="activeClass"
          >
            <div 
  class="icon-container w-5 h-5 flex items-center justify-center"
  v-html="item.icon">
</div>
            <span v-if="!isCollapsed" class="font-medium whitespace-nowrap animate-in fade-in slide-in-from-left-4 duration-500">{{ item.label }}</span>
            
            <div v-if="isCollapsed" class="absolute left-16 bg-slate-900 text-white text-[11px] px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-all duration-300 whitespace-nowrap z-50 shadow-2xl border border-white/10 translate-x-2 group-hover:translate-x-0">{{ item.label }}</div>
          </RouterLink>
        </div>
      </div>

      <div class="space-y-2">
        <h3 v-if="!isCollapsed" class="px-2 text-[10px] font-bold uppercase tracking-[0.2em] text-purple-400/60 mb-4">
          Administration
        </h3>
        <div class="space-y-1">
          <RouterLink
            v-for="item in adminMenu"
            :key="item.label"
            :to="item.to"
            class="flex items-center gap-4 rounded-xl px-4 py-3 text-sm transition-all duration-300 relative text-gray-400 border border-transparent hover:bg-white/5 hover:text-white group"
            :active-class="activeClass"
          >
            <div 
  class="icon-container w-5 h-5 flex items-center justify-center"
  v-html="item.icon">
</div>
            <span v-if="!isCollapsed" class="font-medium whitespace-nowrap animate-in fade-in slide-in-from-left-4 duration-500">{{ item.label }}</span>
            
            <div v-if="isCollapsed" class="absolute left-16 bg-slate-900 text-white text-[11px] px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-all duration-300 whitespace-nowrap z-50 shadow-2xl border border-white/10 translate-x-2 group-hover:translate-x-0">{{ item.label }}</div>
          </RouterLink>
        </div>
      </div>
    </nav>

    <div class="px-4 mb-4">
      <div 
        class="rounded-2xl p-2 transition-all duration-500 border border-transparent"
        :class="[!isCollapsed ? 'bg-white/5 border-white/5 backdrop-blur-md shadow-lg' : '']"
      >
        <div class="flex items-center gap-3">
          <div class="relative min-w-[40px]">
             <img
              src="../assets/vague_bleue_petite.png"
              class="w-10 h-10 rounded-xl object-cover ring-2 ring-indigo-500/20"
            />
            <div class="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-[#0f172a]"></div>
          </div>
          <div v-if="!isCollapsed" class="overflow-hidden animate-in fade-in slide-in-from-left-2 duration-300">
            <p class="text-sm font-semibold truncate text-white">{{ user.prenom }} {{ user.nom }}</p>
            <p class="text-[10px] text-indigo-300 font-medium uppercase tracking-wider opacity-70">{{ user.role || 'Super Admin' }}</p>
          </div>
        </div>
      </div>
    </div>

    <div class="p-4 pt-0 space-y-4">
      <button
        @click="logout"
        class="w-full flex items-center gap-4 px-4 py-3 rounded-xl transition-all duration-300 group text-gray-400 hover:text-rose-400 hover:bg-rose-500/10 border border-transparent hover:border-rose-500/20"
      >
        <svg class="w-5 h-5 transition-transform group-hover:-translate-x-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" x2="9" y1="12" y2="12"/>
        </svg>
        <span v-if="!isCollapsed" class="text-sm font-semibold">Déconnexion</span>
      </button>
      
      <div v-if="!isCollapsed" class="pt-4 text-[9px] text-center text-gray-500 font-medium uppercase tracking-[0.2em] border-t border-white/5 opacity-50">
       © Datafuse Studio 2026
      </div>
    </div>
  </aside>
</template>

<script>
import { RouterLink } from "vue-router";

// Définition des icônes SVG en constantes pour un code propre
const ICONS = {
  dashboard: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="7" height="9" x="3" y="3" rx="1"/><rect width="7" height="5" x="14" y="3" rx="1"/><rect width="7" height="9" x="14" y="12" rx="1"/><rect width="7" height="5" x="3" y="16" rx="1"/></svg>`,
  kiosk: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"/><path d="M3 9h18"/><path d="M9 21V9"/></svg>`,
  screen: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="14" x="2" y="3" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>`,
  media: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.934a.5.5 0 0 0-.777-.416L16 11"/><rect width="14" height="12" x="2" y="6" rx="2"/></svg>`,
  client: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`,
  users: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="14" x="8" y="8" rx="2"/><path d="M4 14c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h8c1.1 0 2 .9 2 2"/></svg>`,
  tools: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>`,
  ticket: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 9V5.2a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2V9a2 2 0 0 0 0 4v3.8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V13a2 2 0 0 0 0-4Z"/><path d="M12 3v18"/><path d="m9 15 3-3 3 3"/><path d="m9 9 3 3 3-3"/></svg>`,
};

export default {
  components: { RouterLink },
  data() {
    return {
      isCollapsed: false,
      user: { prenom: 'MediaLink', nom: 'Solutions', role: 'Admin' },
      infrastructureMenu: [
        { label: "Dashboard", to: "/", icon: ICONS.dashboard },
        { label: "Kiosk", to: "/kiosk", icon: ICONS.kiosk },
        { label: "Ecran", to: "/screen", icon: ICONS.screen },
        { label: "Media", to: "/media-library", icon: ICONS.media },
      ],
      adminMenu: [
        { label: "Client", to: "/client", icon: ICONS.client },
        { label: "Counter & User", to: "/Counter-User", icon: ICONS.users },
        { label: "Support Technique", to: "/maintenance", icon: ICONS.tools },
        { label: "Ticket Client", to: "/ticket", icon: ICONS.ticket },
      ],
      activeClass: "text-indigo-400 text-white shadow-[0_0_20px_rgba(99,102,241,0.2)] border-white/20"
    };
  },
  methods: {
    logout() {
      console.log("Déconnexion...");
    }
  }
};
</script>

<style scoped>
.scrollbar-none::-webkit-scrollbar { display: none; }
.scrollbar-none { -ms-overflow-style: none; scrollbar-width: none; }

/* Styles des liens de navigation */




/* État actif avec gradient */
.active-gradient {
  background: linear-gradient(90deg, rgba(99, 102, 241, 0.15) 0%, rgba(168, 85, 247, 0.05) 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: white !important;
}

.icon-container svg {
  width: 100%;
  height: 100%;
}


/* Animations simples si tailwind-animate n'est pas installé */
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes slideInLeft { from { transform: translateX(-10px); } to { transform: translateX(0); } }
.animate-in { animation: fadeIn 0.4s ease-out; }
.fade-in { animation-name: fadeIn; }
.slide-in-from-left-2 { animation: slideInLeft 0.3s ease-out; }
</style>